import React, { Component } from "react";
import PropTypes from "prop-types";
import { reduxForm, Field, reset } from "redux-form";
import { connect } from "react-redux";
import { withRouter, Redirect } from "react-router";
import { toast } from "react-toastify";
import Multiselect from "multiselect-react-dropdown";

import withAjaxStatus from "../../../support/hoc/withAjaxStatus";
import { required, renderField } from "../../../support/forms";
import Button from "../../../support/components/Button";
import withHistory from "../../../support/hoc/withHistory";

import * as apis from "./apis";
import "../schools/Edit.css";
import "./Edit.css";

const FORM_NAME = "segment";

// Students match on CurrentGrade < 13, so only pre-graduation grades are offered.
const GRADE_OPTIONS = [5, 6, 7, 8, 9, 10, 11, 12].map((g) => ({ name: `Grade ${g}`, value: g }));

const parseZipCodes = (text) => {
    if (!text) {
        return [];
    }
    return text
        .split(/[\s,]+/)
        .map((z) => z.trim())
        .filter((z) => z.length > 0);
};

class Edit extends Component {
    static permission = "Admin.Segments";

    constructor(props) {
        super(props);
        this.state = {
            showComplete: false,
            schoolList: [],
            specialGroupList: [],
            selectedSchools: [],
            selectedGrades: [],
            selectedSpecialGroups: [],
            zipText: "",
            preview: null,
        };
    }

    componentDidMount = async () => {
        this._isMounted = true;
        const { match } = this.props;
        const { id } = match.params;

        this.props.ajaxStatusLoading();
        try {
            const [schoolsResponse, specialGroupsResponse] = await Promise.all([
                apis.getAllSchools(),
                apis.getActiveSpecialGroups(),
            ]);
            const schoolList = schoolsResponse.data.result || [];
            const specialGroupList = specialGroupsResponse.data.result || [];

            if (id === "0") {
                if (this._isMounted) {
                    this.setState({ schoolList, specialGroupList });
                    this.props.initialize({ name: "", description: "" });
                }
                this.props.ajaxStatusComplete();
                return;
            }

            const segment = (await apis.getById(id)).data.result;
            const criteria = segment.criteria || {};
            if (this._isMounted) {
                this.setState({
                    schoolList,
                    specialGroupList,
                    selectedSchools: schoolList.filter((s) => (criteria.schoolIds || []).includes(s.id)),
                    selectedGrades: GRADE_OPTIONS.filter((g) => (criteria.grades || []).includes(g.value)),
                    selectedSpecialGroups: specialGroupList.filter((s) =>
                        (criteria.specialGroupIds || []).includes(s.id)
                    ),
                    zipText: (criteria.zipCodes || []).join(", "),
                });
                this.props.initialize({ name: segment.name, description: segment.description });
            }
            this.props.ajaxStatusComplete();
        } catch (error) {
            this.props.ajaxStatusError(error.message);
        }
    };

    componentWillUnmount = () => {
        this._isMounted = false;
    };

    buildCriteria = () => {
        return {
            schoolIds: this.state.selectedSchools.map((s) => s.id),
            grades: this.state.selectedGrades.map((g) => g.value),
            zipCodes: parseZipCodes(this.state.zipText),
            specialGroupIds: this.state.selectedSpecialGroups.map((s) => s.id),
        };
    };

    onPreview = async () => {
        this.props.ajaxStatusLoading();
        try {
            const preview = (await apis.preview(this.buildCriteria())).data.result;
            if (this._isMounted) {
                this.setState({ preview });
            }
            this.props.ajaxStatusComplete();
        } catch (error) {
            this.props.ajaxStatusError(error);
        }
    };

    onSave = (values) => {
        const { match } = this.props;
        const { id } = match.params;

        const payload = {
            name: values.name,
            description: values.description,
            criteria: this.buildCriteria(),
        };

        let method = "create";
        if (id !== "0") {
            method = "update";
            payload.id = id;
        }

        this.props.ajaxStatusLoading();
        apis[method](payload)
            .then(() => {
                this.props.ajaxStatusComplete();
                this.props.onSaveComplete();
                this.setState({ showComplete: true });
                toast.success("Save Complete");
            })
            .catch((error) => {
                if (error.response && error.response.data && error.response.data.error) {
                    this.props.ajaxStatusError(error.response.data.error.message);
                    return;
                }
                this.props.ajaxStatusError(error.message);
            });
    };

    onCancel = (e) => {
        e.preventDefault();
        this.props.reset();
        this.props.history.goBack();
    };

    render() {
        if (this.state.showComplete) return <Redirect to="/admin/segments" />;
        const { match } = this.props;
        const { id } = match.params;
        const label = id === "0" ? "Add" : "Edit";
        const { preview } = this.state;
        const zipCodes = parseZipCodes(this.state.zipText);

        return (
            <div className="segment-edit">
                <h1>{label}&nbsp;Segment</h1>

                <div className="admin-school-edit-row">
                    <Field
                        name="name"
                        component={renderField}
                        label="Name"
                        validate={[required]}
                        maxlength="200"
                        className="admin-school-edit-input is-active-field"
                    />
                </div>

                <div className="admin-school-edit-row">
                    <Field
                        name="description"
                        component={renderField}
                        label="Description"
                        maxlength="1000"
                        className="admin-school-edit-input is-active-field"
                    />
                </div>

                <h3>Criteria</h3>
                <p className="segment-hint">
                    A student must match <strong>every</strong> criterion below. Leave a criterion empty to
                    place no restriction on that dimension. A segment with no criteria matches no students.
                </p>

                <div className="segment-field">
                    <label>Schools</label>
                    <Multiselect
                        options={this.state.schoolList}
                        selectedValues={this.state.selectedSchools}
                        onSelect={(list) => this.setState({ selectedSchools: list })}
                        onRemove={(list) => this.setState({ selectedSchools: list })}
                        displayValue="name"
                        showCheckbox={true}
                        avoidHighlightFirstOption={true}
                        placeholder="Select school(s)..."
                    />
                </div>

                <div className="segment-field">
                    <label>Grades</label>
                    <Multiselect
                        options={GRADE_OPTIONS}
                        selectedValues={this.state.selectedGrades}
                        onSelect={(list) => this.setState({ selectedGrades: list })}
                        onRemove={(list) => this.setState({ selectedGrades: list })}
                        displayValue="name"
                        showCheckbox={true}
                        avoidHighlightFirstOption={true}
                        placeholder="Select grade(s)..."
                    />
                </div>

                <div className="segment-field">
                    <label>Special Groups</label>
                    <Multiselect
                        options={this.state.specialGroupList}
                        selectedValues={this.state.selectedSpecialGroups}
                        onSelect={(list) => this.setState({ selectedSpecialGroups: list })}
                        onRemove={(list) => this.setState({ selectedSpecialGroups: list })}
                        displayValue="name"
                        showCheckbox={true}
                        avoidHighlightFirstOption={true}
                        placeholder="Select special group(s)..."
                    />
                </div>

                <div className="segment-field">
                    <label>Zip Codes</label>
                    <input
                        type="text"
                        className="segment-zip-input"
                        value={this.state.zipText}
                        onChange={(e) => this.setState({ zipText: e.target.value })}
                        placeholder="Type a zip and press comma or space (e.g. 02903, 02904)"
                    />
                    {zipCodes.length > 0 && (
                        <div className="segment-zip-chips">
                            {zipCodes.map((z) => (
                                <span key={z} className="segment-zip-chip">
                                    {z}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="segment-preview-section">
                    <div className="segment-preview-actions">
                        <Button onClick={this.onPreview} id="Preview" defaultMessage="Preview" />
                    </div>
                    {preview && (
                        <div className="segment-preview-panel">
                            <div className="segment-preview-header">
                                <strong>{preview.count}</strong> matching student(s)
                            </div>
                            {preview.students && preview.students.length > 0 ? (
                                <ul className="segment-preview-list">
                                    {preview.students.map((s) => (
                                        <li key={s.userProfileId}>
                                            <span className="segment-preview-name">{s.name}</span>{" "}
                                            <span className="segment-preview-meta">
                                                (#{s.crusaderId}) &mdash; Grade {s.currentGrade}
                                                {s.schoolName ? `, ${s.schoolName}` : ""}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="segment-preview-empty">No students match these criteria.</div>
                            )}
                            {preview.count > (preview.students ? preview.students.length : 0) && (
                                <div className="segment-preview-footer">
                                    Showing first {preview.students.length} of {preview.count}.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="segment-edit-buttons">
                    <Button onClick={this.props.handleSubmit(this.onSave)} id="Save" defaultMessage="Save" />
                    <a href="/admin/segments" onClick={(e) => this.onCancel(e)}>
                        Cancel
                    </a>
                </div>
            </div>
        );
    }
}

Edit.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    onSaveComplete: PropTypes.func.isRequired,
};

Edit = reduxForm({
    form: FORM_NAME,
})(Edit);

const mapDispatchToProps = (dispatch) => {
    return {
        reset: () => dispatch(reset(FORM_NAME)),
    };
};

Edit = connect(null, mapDispatchToProps)(Edit);

Edit = withRouter(Edit);
Edit = withAjaxStatus(Edit);
Edit = withHistory(Edit);

export default Edit;
